import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/lib/auth';
import { leadsAPI, employeesAPI, reportsAPI, downloadCSV } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { AddLeadDialog, FollowUpDialog, LeadDetailDialog } from './LeadDialogs';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/errors';
import { LeadTriageBadge } from '@/components/admin/ai/LeadTriageBadge';
import { 
  Users, Plus, Upload, Search, Filter, Phone, Mail, MapPin,
  Calendar, Clock, CheckCircle, XCircle, UserPlus, Loader2,
  ChevronDown, ChevronUp, MessageSquare, TrendingUp, Target,
  ArrowRight, RefreshCw, Trash2, Edit, Eye, FileText, StickyNote, Download
} from 'lucide-react';

const LEAD_SOURCES = [
  { value: 'manual', label: 'Manual Entry' },
  { value: 'website', label: 'Website Form' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'phone', label: 'Phone Call' },
];

const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'follow_up', label: 'Follow Up', color: 'bg-orange-100 text-orange-700' },
  { value: 'interested', label: 'Interested', color: 'bg-teal-100 text-teal-700' },
  { value: 'potential', label: 'Potential', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'application_submitted', label: 'Application Submitted', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'future_student', label: 'Future Student', color: 'bg-lime-100 text-lime-700' },
  { value: 'converted', label: 'Converted', color: 'bg-green-100 text-green-700' },
  { value: 'not_eligible', label: 'Not Eligible', color: 'bg-gray-100 text-gray-700' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-rose-100 text-rose-700' },
  { value: 'not_responding', label: 'Not Responding', color: 'bg-amber-100 text-amber-700' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' },
];

const COUNTRIES = ['United Kingdom', 'Ireland', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Other Europe'];

export const LeadManagement = () => {
  const { user } = useAuth();
  
  // Check if user is super admin (no employee_id = super admin)
  const isSuperAdmin = user?.is_super_admin || (user?.role === 'admin' && !user?.employee_id);
  
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [serverIsSuperAdmin, setServerIsSuperAdmin] = useState(false); // From API response
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Form states - moved to extracted dialog components for performance
  const [saving, setSaving] = useState(false);
  
  // Bulk selection
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  
  // Follow-up form - state moved to FollowUpDialog
  
  // New note input - state moved to LeadDetailDialog

  useEffect(() => {
    fetchData();
  }, [statusFilter, sourceFilter, assignedFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (assignedFilter !== 'all') params.assigned_to = assignedFilter;
      if (searchTerm) params.search = searchTerm;
      
      const [leadsRes, employeesRes] = await Promise.all([
        leadsAPI.getAll(params),
        employeesAPI.getAll()
      ]);
      
      setLeads(leadsRes.data.leads || []);
      setStats(leadsRes.data.stats || {});
      setServerIsSuperAdmin(leadsRes.data.is_super_admin || false);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      await leadsAPI.update(leadId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async (leadId, employeeId) => {
    try {
      await leadsAPI.assign(leadId, employeeId);
      toast.success('Lead assigned');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign lead');
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignTo || selectedLeads.length === 0) {
      toast.error('Select leads and an employee');
      return;
    }
    
    try {
      await leadsAPI.bulkAssign(selectedLeads, bulkAssignTo);
      toast.success(`Assigned ${selectedLeads.length} leads`);
      setSelectedLeads([]);
      setBulkAssignTo('');
      fetchData();
    } catch (error) {
      toast.error('Failed to bulk assign');
    }
  };

  const handleConvert = async (leadId) => {
    if (!window.confirm('Convert this lead to a student account?')) return;
    
    try {
      const res = await leadsAPI.convert(leadId);
      toast.success(`Lead converted! Temp password: ${res.data.temp_password}`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to convert lead'));
    }
  };

  // Determine if user can assign leads (super admin OR has leads_assign permission)
  const canAssignLeads = isSuperAdmin || serverIsSuperAdmin || user?.permissions?.leads_assign;

  const getStatusBadge = (status) => {
    const statusInfo = LEAD_STATUSES.find(s => s.value === status) || LEAD_STATUSES[0];
    return <Badge className={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  // Bulk Upload Handler — accepts BOTH .csv and .xlsx.
  // Routes everything through the robust backend importer which uses pandas
  // for CSV parsing (handles quoted fields with newlines/commas correctly)
  // and applies dedup by phone + counselor→user mapping.
  const handleBulkUpload = async (file) => {
    const lname = (file?.name || '').toLowerCase();
    const isCsv = lname.endsWith('.csv');
    const isXlsx = lname.endsWith('.xlsx') || lname.endsWith('.xls');
    if (!isCsv && !isXlsx) {
      toast.error('Please upload a CSV or XLSX file');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('intake_label', 'September 2026');
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/leads/import-xlsx`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      const s = data.stats || {};
      toast.success(
        `${s.inserted || 0} leads added · ${s.skipped_duplicate_in_file || 0} in-file duplicates · ${s.skipped_duplicate_in_db || 0} already in DB`,
        { duration: 8000 },
      );
      setShowBulkDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to upload file');
    }
  };

  // Reassign existing leads to their real officer accounts. Matches
  // ``assigned_to_name`` (from the uploaded spreadsheet's COUNSELOR column)
  // against actual DB users by full-name + email tokens, creating placeholder
  // employee accounts when a counselor doesn't exist yet. Force mode also
  // re-runs on already-assigned leads to fix earlier bad mappings.
  const handleReassignCounselors = async () => {
    const confirmMsg = 'Match uploaded counselor names to real staff accounts?\n\n' +
      '• Leads with an existing counselor account will link directly.\n' +
      '• Missing counselors will get a placeholder account (they can reset password later).\n' +
      '• Any previously auto-created placeholder that is no longer needed will be removed.\n\n' +
      'Safe to run more than once.';
    if (!window.confirm(confirmMsg)) return;
    try {
      setReassigning(true);
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/leads/backfill-counselor-assignments?force=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reassignment failed');
      const byC = data.assignments_by_counselor || {};
      const total = Object.values(byC).reduce((a, b) => a + b, 0);
      const summary =
        `${data.leads_reassigned} leads updated · ${total} now tagged to ${Object.keys(byC).length} officers` +
        (data.placeholder_users_removed ? ` · ${data.placeholder_users_removed} obsolete placeholders removed` : '');
      toast.success(summary, { duration: 10000 });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Reassignment failed');
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Leads</p>
                <p className="text-2xl font-bold text-blue-700">{stats.total || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">New</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.new || 0}</p>
              </div>
              <Plus className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Qualified</p>
                <p className="text-2xl font-bold text-purple-700">{stats.qualified || 0}</p>
              </div>
              <Target className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Converted</p>
                <p className="text-2xl font-bold text-green-700">{stats.converted || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-teal-600">Conversion</p>
                <p className="text-2xl font-bold text-teal-700">{stats.conversion_rate || 0}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-teal-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 p-4 rounded-xl border">
        <Button onClick={() => setShowAddDialog(true)} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="w-4 h-4 mr-2" /> Add Lead
        </Button>
        <Button onClick={() => setShowBulkDialog(true)} variant="outline">
          <Upload className="w-4 h-4 mr-2" /> Bulk Upload
        </Button>
        {canAssignLeads && (
          <Button
            onClick={handleReassignCounselors}
            variant="outline"
            disabled={reassigning}
            className="border-teal-300 text-teal-700 hover:bg-teal-50 dark:text-teal-300"
            data-testid="reassign-counselors-btn"
            title="Match uploaded lead counselor names to real staff accounts. Safe to run multiple times."
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${reassigning ? 'animate-spin' : ''}`} />
            {reassigning ? 'Reassigning…' : 'Reassign Leads to Officers'}
          </Button>
        )}
        
        <div className="flex-1" />
        
        {/* Search */}
        <div className="flex gap-2">
          <Input
            placeholder="Search ref code, name, phone, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button variant="outline" onClick={handleSearch}>
            <Search className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Filters */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {LEAD_STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {LEAD_SOURCES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {canAssignLeads && employees.length > 0 && (
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger className="w-44" data-testid="assigned-to-filter">
              <SelectValue placeholder="Assigned To" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                  {emp.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
        
        {canAssignLeads && (
          <Button
            variant="outline"
            onClick={async () => {
              try {
                toast.info('Generating leads report...');
                const params = {};
                if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
                if (sourceFilter && sourceFilter !== 'all') params.source = sourceFilter;
                if (assignedFilter && assignedFilter !== 'all') params.assigned_to = assignedFilter;
                await downloadCSV(reportsAPI.exportLeads(params), `leads_report_${new Date().toISOString().slice(0,10)}.csv`);
                toast.success('Leads report downloaded');
              } catch (err) {
                const msg = err?.message || 'Failed to download report';
                toast.error(msg);
              }
            }}
            className="gap-2"
            data-testid="download-leads-csv"
          >
            <Download className="w-4 h-4" />
            <span className="hidden lg:inline">Export</span>
          </Button>
        )}
      </div>

      {/* Bulk Actions - Only for Super Admins */}
      {selectedLeads.length > 0 && canAssignLeads && (
        <div className="flex items-center gap-3 bg-teal-50 p-3 rounded-lg border border-teal-200">
          <span className="text-sm font-medium text-teal-700">
            {selectedLeads.length} lead(s) selected
          </span>
          <Select value={bulkAssignTo} onValueChange={setBulkAssignTo}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Assign to..." />
            </SelectTrigger>
            <SelectContent>
              {employees.map(emp => (
                <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                  {emp.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleBulkAssign} size="sm" className="bg-teal-600 hover:bg-teal-700">
            Assign Selected
          </Button>
          <Button onClick={() => setSelectedLeads([])} size="sm" variant="ghost">
            Clear
          </Button>
        </div>
      )}
      
      {/* Info banner for employees */}
      {!canAssignLeads && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <Eye className="w-4 h-4 inline mr-2" />
            You are viewing leads assigned to you. Contact an admin to reassign leads.
          </p>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No leads found</p>
            <Button onClick={() => setShowAddDialog(true)} className="mt-4">
              <Plus className="w-4 h-4 mr-2" /> Add First Lead
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-700">
                <tr>
                  {canAssignLeads && (
                    <th className="p-3 text-left">
                      <Checkbox 
                        checked={selectedLeads.length === leads.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Ref Code</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Lead</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Contact</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Interest</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Source</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Assigned To</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id} className="border-t hover:bg-gray-50 dark:hover:bg-slate-700">
                    {canAssignLeads && (
                      <td className="p-3">
                        <Checkbox 
                          checked={selectedLeads.includes(lead.id)}
                          onCheckedChange={() => toggleSelectLead(lead.id)}
                        />
                      </td>
                    )}
                    <td className="p-3">
                      <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap" data-testid={`lead-ref-code-${lead.id}`}>
                        {lead.reference_code || `${lead.id?.slice(0, 8)}…`}
                      </span>
                    </td>
                    <td className="p-3">
                      <p className="font-medium text-gray-800 dark:text-white">{lead.full_name}</p>
                      <p className="text-xs text-gray-500">{new Date(lead.created_at).toLocaleDateString()}</p>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1 text-sm">
                        <span className="flex items-center gap-1 text-gray-600">
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </span>
                        {lead.email && (
                          <span className="flex items-center gap-1 text-gray-500 text-xs">
                            <Mail className="w-3 h-3" /> {lead.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-sm">{lead.country_interest || '-'}</span>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="text-xs">
                        {LEAD_SOURCES.find(s => s.value === lead.source)?.label || lead.source}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Select value={lead.status} onValueChange={(v) => handleUpdateStatus(lead.id, v)}>
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_STATUSES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      {lead.assigned_to ? (
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{lead.assigned_to_name}</span>
                          {lead.ai_triage && <span className="mt-1"><LeadTriageBadge triage={lead.ai_triage} testId={`lead-triage-badge-${lead.id}`} /></span>}
                          {canAssignLeads && (
                            <Select onValueChange={(v) => handleAssign(lead.id, v)}>
                              <SelectTrigger className="w-28 h-6 text-xs mt-1">
                                <SelectValue placeholder="Reassign..." />
                              </SelectTrigger>
                              <SelectContent>
                                {employees.map(emp => (
                                  <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                                    {emp.full_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      ) : canAssignLeads ? (
                        <Select onValueChange={(v) => handleAssign(lead.id, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue placeholder="Assign..." />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map(emp => (
                              <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                                {emp.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setSelectedLead(lead); setShowDetailDialog(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setSelectedLead(lead); setShowFollowUpDialog(true); }}
                        >
                          <Calendar className="w-4 h-4" />
                        </Button>
                        {lead.status !== 'converted' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-green-600"
                            onClick={() => handleConvert(lead.id)}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Lead Dialog - extracted component for performance */}
      <AddLeadDialog open={showAddDialog} onOpenChange={setShowAddDialog} onSuccess={fetchData} />

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" />
              Bulk Upload Leads
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <p className="text-sm font-semibold text-gray-700">Supported formats:</p>
              <div className="space-y-2 text-xs text-gray-600">
                <div>
                  <span className="inline-block bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-semibold mr-1">XLSX</span>
                  H E Direct <em>Leads Data</em> workbook — columns:
                  <code className="block bg-gray-200 p-2 rounded mt-1">
                    STUDENT NAME, MOBILE NO, CITY, HED OFFICE, LEAD SOURCE, STATUS, DESTINATION, COUNSELOR, NOTE 1-3
                  </code>
                </div>
                <div>
                  <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold mr-1">CSV</span>
                  Simple format:
                  <code className="block bg-gray-200 p-2 rounded mt-1">
                    full_name,phone,email,country_interest,source,notes
                  </code>
                </div>
              </div>
            </div>
            <Input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => e.target.files[0] && handleBulkUpload(e.target.files[0])}
              data-testid="bulk-upload-file-input"
            />
            <p className="text-xs text-gray-500">
              Duplicate phone numbers will be skipped automatically (both within the file and against existing leads).
              XLSX uploads are auto-tagged as <strong>September 2026</strong> intake.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog - extracted component for performance */}
      <LeadDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        lead={selectedLead}
        onNoteAdded={async () => {
          const updatedLead = await leadsAPI.getById(selectedLead.id);
          setSelectedLead(updatedLead.data);
          fetchData();
        }}
        getStatusBadge={getStatusBadge}
      />

      {/* Follow-up Dialog - extracted component for performance */}
      <FollowUpDialog
        open={showFollowUpDialog}
        onOpenChange={setShowFollowUpDialog}
        lead={selectedLead}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default LeadManagement;
